/**
 * PageGuards Component
 * Wrapper component that applies page guards to protected routes
 * Checks partner status and terms agreement compliance
 */

import React, { useState, useEffect } from 'react';
import { usePageGuards } from './usePageGuards';
import { LoadingSpinner } from 'feSharedIndex';

/**
 * PageGuards wrapper component
 * Applies guards to children and shows loading state during checks
 * @param {object} props
 * @param {React.ReactNode} props.children - Child components to wrap
 */
const PageGuards = ({ children }) => {
  const { isChecking } = usePageGuards();
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);

  // Check if onboarding overlay is active to prevent flickering
  useEffect(() => {
    const checkOnboarding = () => {
      // Check if onboarding overlay exists and is visible
      const overlay = document.querySelector('.onboarding-overlay');
      const isActive = overlay && (
        overlay.classList.contains('visible') || 
        overlay.style.display !== 'none'
      );
      setIsOnboardingActive(isActive);
    };

    // Check immediately
    checkOnboarding();

    // Use MutationObserver to watch for onboarding overlay changes
    // Use a more aggressive observer to catch rapid changes
    const observer = new MutationObserver(() => {
      // Use requestAnimationFrame to batch rapid changes
      requestAnimationFrame(() => {
        requestAnimationFrame(checkOnboarding);
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });

    // Also check periodically as a fallback (very fast check)
    const interval = setInterval(checkOnboarding, 50); // Check every 50ms

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  // Don't show loading spinner if onboarding is active - prevents flickering
  if (isChecking && !isOnboardingActive) {
    return (
      <LoadingSpinner message="Verifying access..." />
    );
  }

  // Render children once checks are complete or if onboarding is active
  return <>{children}</>;
};

export default PageGuards;

